import { apiClient } from "@/lib/api/api-client";
import { createStorageService } from "@/lib/storage/gcs/googleCloudStorage";
import { IStorageService } from "@/lib/storage/storage";
import {
  EmployeeFullInfo,
  EmployeeInfo,
  EmployeeInfoUpdate,
  InterviewRateEmployee,
  BusinessEmployee,
} from "@/modules/types/employee";
import { BaseResponse } from "@/modules/types/response";

interface EmployeeResp extends BaseResponse {
  employeeInfo: EmployeeInfo;
}

export interface CandidateProfileResp extends BaseResponse {
  employeeInfo: EmployeeFullInfo;
}

export interface RateInterviewPerformanceResp extends BaseResponse {
  interviewRateEmployee: InterviewRateEmployee;
}

export interface GetRateInterviewResp extends BaseResponse {
  interviewRateEmployee: InterviewRateEmployee;
}

export interface BusinessEmployeesResponse extends BaseResponse {
  employees: BusinessEmployee[];
  total: number;
  page: number;
  limit: number;
}

export class employeeService {
  private BASE_PATH =
    process.env.API_EMPLOYEEE_ENDPOINT || "http://localhost:8003/employee";
  private apiClient = apiClient;
  private apiVersion = "v1.0.0";
  private storage: IStorageService = createStorageService();
  private destination = "employee/";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async getByDefault(): Promise<EmployeeResp | undefined> {
    return this.apiClient.fetch<EmployeeResp>(
      `${this.BASE_PATH}/${this.apiVersion}/information`
    );
  }

  async businessGetCandidateProfile(
    businessId: string,
    candidateId: string
  ): Promise<CandidateProfileResp> {
    return await this.apiClient.fetch<CandidateProfileResp>(
      `${this.BASE_PATH}/${this.apiVersion}/business/${businessId}/view-profile/${candidateId}`
    );
  }

  async rateInterviewPerformance(
    businessId: string,
    candidateId: string,
    interviewId: string,
    rating: number,
    comment?: string
  ): Promise<RateInterviewPerformanceResp> {
    return await this.apiClient.fetch<RateInterviewPerformanceResp>(
      `${this.BASE_PATH}/${this.apiVersion}/interview-rate`,
      {
        method: "POST",
        body: JSON.stringify({
          employeeId: candidateId,
          businessRateId: businessId,
          rate: rating,
          interviewId,
          comment,
        }),
      }
    );
  }

  async getRateInterview(interviewId: string): Promise<GetRateInterviewResp> {
    return await this.apiClient.fetch<GetRateInterviewResp>(
      `${this.BASE_PATH}/${this.apiVersion}/interview-rate/${interviewId}`
    );
  }

  async uploadAvatar(
    employeeId: string,
    file: File,
    fileName: string
  ): Promise<string> {
    const filePath = `${this.destination}${employeeId}/avatar/${fileName}`;
    return this.storage.uploadPublicFile(file, filePath);
  }

  async updateAvatar(
    employeeId: string,
    avatar: string
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/information`,
      {
        method: "PUT",
        body: JSON.stringify({ id: employeeId, avatar }),
      }
    );
  }

  async update(
    employeeId: string,
    employeeInfo: EmployeeInfoUpdate
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/information`,
      {
        method: "PUT",
        body: JSON.stringify({ ...employeeInfo, id: employeeId }),
      }
    );
  }

  async getBusinessEmployees(
    businessId: string,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<BusinessEmployeesResponse> {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (search) params.set("search", search);

    return await this.apiClient.fetch<BusinessEmployeesResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/business/${businessId}/employees?${params.toString()}`,
    );
  }

  async deleteEmployee(employeeId: string): Promise<BaseResponse> {
    return await this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/employees/${employeeId}`,
      { method: "DELETE" },
    );
  }

  async setEmployeeExpiry(
    employeeId: string,
    expiredAt: number,
  ): Promise<BaseResponse> {
    return await this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/employees/${employeeId}/expiry`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ expiredAt }),
      },
    );
  }

  async generateShareProfileLink(
    businessId: string,
    employeeId: string,
    jobApplyId: string,
  ): Promise<{ sessionId: string } & BaseResponse> {
    return await this.apiClient.fetch<{ sessionId: string } & BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/business/share-profile`,
      {
        method: "POST",
        body: JSON.stringify({ businessId, employeeId, jobApplyId }),
      }
    );
  }

  async getInterviewRates(
    businessId: string,
    candidateId: string,
    jobApplyId: string
  ): Promise<{ code: number; message: string; interviewRateEmployee: InterviewRateEmployee[] }> {
    return await this.apiClient.fetch<{ code: number; message: string; interviewRateEmployee: InterviewRateEmployee[] }>(
      `${this.BASE_PATH}/${this.apiVersion}/business/${businessId}/candidate/${candidateId}/job-apply/${jobApplyId}/interview-rate`
    );
  }
}

export const employeeServiceInstance = new employeeService();
